import React from 'react';
import { motion } from 'motion/react';
import { ArrowRight, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import GreenButton from '../components/home/GreenButton';
import HowItWorks from '../components/home/HowItWorks';
import SlimGridMotion from '../components/SlimGridMotion';
import UspStrip from '../components/services/UspStrip';
import PrintingFontStyles from '../components/printing/PrintingFontStyles';
import Reveal from '../components/home/Reveal';
import SectionHeading from '../components/home/SectionHeading';
import { useContactModal } from '../contexts/ContactModalContext';
import { printingColors, printingProcessSteps } from '../lib/printing-theme';
import { PRINT_CATEGORIES } from '../data/printing/catalogue';

// Hero grid: local print mockups (see public/home/print)
const heroGridImages = [
  '/home/print/business-cards.jpg',
  '/home/print/flyers.jpg',
  '/home/print/booklets.jpg',
  '/home/print/posters.jpg',
  '/home/print/greetings-cards.jpg',
  '/home/print/gift-vouchers.jpg',
  '/home/print/product-labels.jpg',
  '/home/print/wall-calendars.jpg',
  '/home/print/scratch-cards.jpg',
  '/home/print/table-talkers.jpg',
  '/home/print/appointment-cards.jpg',
  '/home/print/art-prints.jpg',
];

const gridImages = [...heroGridImages, ...heroGridImages, ...heroGridImages].slice(0, 21);

// Rotating USP bar items requested by client
const USP_ITEMS = [
  'NO MINIMUM ORDER',
  'FAST TURNAROUND',
  'FREE ARTWORK CHECK',
  'IN-HOUSE DESIGN',
  'WHOLESALE DISCOUNTS',
  'SAMPLE PACKS',
  'PREMIUM FINISHES',
];

// 7 category images matching PRINT_CATEGORIES order
const categoryImages: Record<string, string> = {
  'stickers-labels': '/home/print/product-labels.jpg',
  'business-stationery': '/home/print/business-cards.jpg',
  'events': '/home/print/flyers.jpg',
  'cards-invites': '/home/print/greetings-cards.jpg',
  'art': '/home/print/art-prints.jpg',
  'calendars': '/home/print/wall-calendars.jpg',
  'packaging': '/home/print/booklets.jpg',
};

// Supported Q&As on black background (Vehicles style)
const PRINT_FAQS = [
  {
    q: 'What file formats do you accept for print?',
    a: 'We accept high-resolution PDFs, Illustrator (AI/EPS) vector files, and Canva share links. Our design team carries out a free artwork check before production.',
  },
  {
    q: 'What is your turnaround time?',
    a: 'Turnaround depends on product specifications and quantities. Contact our team to confirm turnaround and check express availability.',
  },
  {
    q: 'Can I collect my order directly?',
    a: 'Yes, we offer FREE collection from our Kidderminster showroom, or direct courier delivery across the UK.',
  },
  {
    q: 'Do you offer design and template help?',
    a: 'Yes. We provide standard design templates, Canva setup guidance, and an in-house bespoke design service if you need artwork created from scratch.',
  },
  {
    q: 'Can I request a sample pack before ordering?',
    a: 'Absolutely. We can supply paper stock samples and finish swatches so you can check paper weights and lamination in person.',
  },
];

const PrintingLandingPage: React.FC = () => {
  const { openContactModal } = useContactModal();

  return (
    <>
      <PrintingFontStyles />

      <div className="min-h-screen bg-white">
        {/* Hero: grid motion tile wall */}
        <section className="relative overflow-hidden bg-black text-white min-h-[70svh] lg:h-[calc(60svh_-_var(--header-h,0px))] lg:min-h-[540px] flex items-center">
          <div className="absolute inset-0 isolate">
            <SlimGridMotion items={gridImages} gradientColor="transparent" />
          </div>
          <div className="absolute inset-0 z-[1] bg-gradient-to-r from-black/95 via-black/70 to-black/30" />
          <div className="absolute inset-0 z-[1] bg-gradient-to-t from-black/70 via-transparent to-black/40" />

          <div className="relative z-10 w-full max-w-6xl mx-auto px-6 py-16">
            <div className="max-w-3xl">
              <div className="flex items-center gap-5 mb-5">
                <span className="h-px w-12 bg-[#78BE20]" />
                <span className="font-poppins text-[11px] md:text-xs font-semibold tracking-[0.25em] uppercase">
                  Printing
                </span>
              </div>
              <h1 className="font-montserrat font-extrabold leading-[1.02] tracking-[-0.03em] text-[clamp(34px,4vw,60px)]">
                Bring your artwork to life with{' '}
                <span className="text-[#78BE20]">high-quality, vibrant prints.</span>
              </h1>
              <p className="font-poppins text-base md:text-lg leading-7 text-white/80 mt-6 max-w-xl">
                Business cards to booklets, flyers to packaging. Quality digital printing with fast turnaround
                from our own presses in Kidderminster.
              </p>
              <div className="flex flex-wrap gap-3 mt-8">
                <GreenButton to="/printing/all">Explore Options</GreenButton>
                <GreenButton onClick={openContactModal} variant="outline" className="text-white">
                  Get in Touch
                </GreenButton>
              </div>
            </div>
          </div>
        </section>

        {/* What do we print? - 8 Category Boxes */}
        <section id="categories" className="py-20 md:py-24 px-6 md:px-12 lg:px-24 bg-[#f8f8f8]">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="hearns-font text-4xl md:text-5xl lg:text-6xl mb-4" style={{ color: printingColors.dark }}>
                What do we print?
              </h2>
              <p className="neuzeit-light-font text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
                Select a category to browse our print range, or explore all products
              </p>
            </div>

            {/* 8 boxes grid: 7 filter categories in order + 8th View all */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6">
              {PRINT_CATEGORIES.map((category, index) => (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.35, delay: index * 0.04 }}
                >
                  <Link
                    to={`/printing/all?category=${category.id}`}
                    className="group block"
                  >
                    <div className="relative aspect-square rounded-[15px] overflow-hidden bg-gray-200 mb-3">
                      <img
                        src={categoryImages[category.id] || gridImages[0]}
                        alt={category.label}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div
                        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        style={{ backgroundColor: `${printingColors.accent}30` }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div
                          className="w-12 h-12 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: printingColors.accent }}
                        >
                          <ArrowRight className="w-6 h-6 text-white" />
                        </div>
                      </div>
                    </div>
                    <h3
                      className="neuzeit-font text-base md:text-lg font-semibold text-center group-hover:text-[#64a70b] transition-colors"
                      style={{ color: printingColors.dark }}
                    >
                      {category.label}
                    </h3>
                  </Link>
                </motion.div>
              ))}

              {/* 8th Box: View All */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.35, delay: 0.32 }}
              >
                <Link
                  to="/printing/all"
                  className="group block"
                >
                  <div
                    className="relative aspect-square rounded-[15px] overflow-hidden mb-3 flex items-center justify-center transition-all duration-300 group-hover:scale-[1.02]"
                    style={{ backgroundColor: printingColors.accent }}
                  >
                    <div className="text-center p-4">
                      <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-3 group-hover:bg-white/30 transition-colors">
                        <ArrowRight className="w-7 h-7 text-white group-hover:translate-x-1 transition-transform" />
                      </div>
                      <span className="hearns-font text-xl md:text-2xl text-white">
                        View all
                      </span>
                    </div>
                  </div>
                  <h3 className="neuzeit-font text-base md:text-lg font-semibold text-center text-[#64a70b]">
                    Browse All Products
                  </h3>
                </Link>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Green Rotating USP Bar */}
        <UspStrip items={USP_ITEMS} />

        {/* How It Works - Black background */}
        <HowItWorks tone="dark" steps={printingProcessSteps.map((s) => ({ title: s.title, text: s.description }))} />

        {/* Case Studies Section: "A few of our recent projects" */}
        <section className="py-20 md:py-24 px-6 md:px-12 lg:px-24 bg-white border-t border-black/10">
          <div className="max-w-6xl mx-auto">
            <Reveal className="mb-10 text-center">
              <SectionHeading
                title="A few of our recent projects"
                subtitle="Proudly supporting local and national customers at any point of their journey."
                tone="green"
                underline
              />
            </Reveal>

            {/* Real verified print project from home page */}
            <Reveal delay={100}>
              <div className="relative overflow-hidden bg-black text-white min-h-[420px] lg:min-h-[500px] flex items-end rounded-[15px]">
                <img
                  src="/home/hero/printing.jpg"
                  alt="The Green Room Café"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10" />
                <div className="relative z-10 p-8 md:p-10 max-w-xl">
                  <h3 className="font-montserrat font-extrabold text-2xl md:text-[32px] tracking-tight leading-tight mb-3">
                    The Green Room Café
                  </h3>
                  <p className="font-poppins font-semibold text-sm md:text-base mb-2">
                    Menus, loyalty cards, table talkers and more.
                  </p>
                  <p className="font-poppins text-sm leading-6 text-white/80 mb-6">
                    A cohesive print identity for a Kidderminster café, from folded menus to a pavement A-board.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <GreenButton to="/case-study/green-room-cafe" className="w-full sm:w-auto justify-center">
                      Read more…
                    </GreenButton>
                    <GreenButton to="/printing/all" className="w-full sm:w-auto justify-center">
                      Browse print products
                    </GreenButton>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* Questions, answered - Black background (Vehicles style) */}
        <section className="py-20 md:py-24 px-6 bg-black text-white border-t border-white/10">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-10 lg:gap-20 items-start">
            <Reveal>
              <h2 className="font-montserrat font-extrabold tracking-[-0.03em] leading-[1.05] text-[clamp(30px,3.4vw,44px)]">
                Questions, answered
              </h2>
              <p className="font-poppins text-sm md:text-base text-white/70 mt-4 leading-relaxed">
                Everything you need to know about placing a print order with Outpost Custom.
              </p>
            </Reveal>

            <Reveal delay={100}>
              <div className="divide-y divide-white/10 border-y border-white/10">
                {PRINT_FAQS.map((faq) => (
                  <details key={faq.q} className="group">
                    <summary className="flex items-center justify-between gap-6 py-5 cursor-pointer list-none font-montserrat font-bold text-base md:text-lg [&::-webkit-details-marker]:hidden">
                      {faq.q}
                      <span className="shrink-0 w-5 h-5 text-white/40 transition-transform duration-500 group-open:rotate-45 group-open:text-[#78BE20]">
                        <Plus className="w-5 h-5" />
                      </span>
                    </summary>
                    <div className="pb-6">
                      <p className="font-poppins text-sm md:text-base leading-7 text-white/75 max-w-xl">
                        {faq.a}
                      </p>
                    </div>
                  </details>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        {/* Ready to get started - Green section */}
        <section className="w-full py-20 md:py-24 px-6 bg-[#78BE20] text-white">
          <div className="max-w-[900px] mx-auto text-center">
            <h2 className="font-montserrat font-extrabold tracking-[-0.03em] leading-[1.05] text-[clamp(36px,5vw,64px)] mb-4">
              Ready to get started?
            </h2>
            <p className="font-poppins text-white/90 text-base md:text-lg mb-10 max-w-xl mx-auto">
              Get in touch with our print team today for a free quote or browse our full catalogue.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                type="button"
                onClick={openContactModal}
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-black font-poppins font-semibold uppercase tracking-[0.12em] text-[11px] hover:bg-black hover:text-white transition-colors"
              >
                Get in touch
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </button>
              <Link
                to="/printing/all"
                className="inline-flex items-center gap-2 px-7 py-3.5 border border-white text-white font-poppins font-semibold uppercase tracking-[0.12em] text-[11px] hover:bg-white hover:text-black transition-colors"
              >
                Browse all products
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default PrintingLandingPage;
